<template>
  <div class="info-modal informer">
    <div class="info-modal__actions">
      <a-select :value="selectedLanguage" size="small" @change="handleLangChange" class="lang-select">
        <a-select-option v-for="lang in langOptions" :key="lang.value">
          {{ lang.name }}
        </a-select-option>
      </a-select>
    </div>
    <div class="info-modal__content">
      <div class="info-modal__image-container">
        <img v-if="selectedImage" class="info-modal__image" :src="selectedImage.path" :alt="informerContext.name" />
      </div>
      <div class="info-modal__info-container">
        <div class="info-modal__grid">
          <div class="info-modal__util-width">
            <div class="info-modal__title">Name (for internal usage)</div>
            <span class="mr-2">{{ informerContext.name }}</span>
            <a-tooltip title="Copy to clipboard" placement="right">
              <a href="javascript:;"><a-icon type="copy" class="font-size-16 text-primary"
                  @click="clipboardHandle('Name', informerContext.name)" /></a>
            </a-tooltip>
          </div>
          <div>
            <div class="d-flex align-items-center">
              <div class="info-modal__title mr-2">Content type</div>
            </div>
            <div class="d-flex">
              <span v-if="informerContext.content_type" class="mr-2">{{ informerContext.content_type }}</span>
              <span v-else class="mr-2">-</span>
            </div>
          </div>
        </div>
        <div class="info-modal__grid">
          <div class="info-modal__util-width">
            <div class="info-modal__title">Title</div>
            <span class="mr-2">{{ stripHtml(selectedInfo.title) }}</span>
          </div>
          <div>
            <div class="d-flex align-items-center">
              <div class="info-modal__title mr-2">Section</div>
            </div>
            <div class="d-flex">
              <div v-if="Array.isArray(informerContext.displays) && informerContext.displays.length"
                style="line-height: 1.8rem">
                <a-tag class="info-modal--tag" v-for="display in informerContext.displays" :key="display.id">
                  {{ displays[display.id] }}
                </a-tag>
              </div>
              <div v-else>-</div>
            </div>
          </div>
        </div>
        <div>
          <div class="info-modal__title">Text</div>
          <span v-if="selectedInfo.description" class="mr-2">{{ stripHtml(selectedInfo.description) }}</span>
          <span v-else>-</span>
        </div>
        <div>
          <div class="d-flex align-items-center">
            <div class="info-modal__title mr-2">Schedule Periods</div>
          </div>
          <div style="position: relative; display: inline-flex; flex-direction: column; align-items: flex-start; gap: 0.2rem;">
            <template>
              <a-tag
                v-for="(period, idx) in informerContext.periods.slice(0, 2)"
                :key="idx + period.starts_at"
                class="info-modal--tag"
              >
                {{ formatDate(period.starts_at) }} - {{ formatDate(period.ends_at) }}
                {{ formatTimezone(period.time_mode) }}
              </a-tag>
            </template>
            <template v-if="informerContext.periods.length > 2 && showAllPeriods">
              <a-tag
                v-for="(period, idx) in informerContext.periods.slice(2)"
                :key="idx"
                class="info-modal--tag"
              >
                {{ formatDate(period.starts_at) }} - {{ formatDate(period.ends_at) }}
                {{ formatTimezone(period.time_mode) }}
              </a-tag>
            </template>
            <a-button
              v-if="informerContext.periods.length > 2"
              type="link"
              style="
                position: absolute;
                right: 0;
                bottom: 0;
                transform: translateX(100%) translateY(-25%);
                line-height: 1;
                height: auto;
                padding: 0;
              "
              @click="() => showAllPeriods = !showAllPeriods"
            >
              {{ showAllPeriods ? 'Hide' : 'View all' }}
            </a-button>
          </div>
        </div>
        <div>
          <div class="info-modal__title">Apps</div>
          <AppsCollapse :apps="informerContext.applications" />
        </div>
      </div>
    </div>
    <a-row>
      <a-col :span="24">
        <div class="d-flex justify-content-between info-modal__footer">
          <a-button @click="clipboardHandle('Informer', JSON.stringify(informerInfo, null, 2) )">
            <a-icon type="copy" class="font-size-14" />
            <span>Copy content</span>
          </a-button>
          <div class="ml-auto text-right">
            <a-button @click="openEditPage">
              <a-icon type="edit" class="font-size-14" />
              <span>Edit</span>
            </a-button>
            <a-button type="primary" style="margin-left: 10px;" @click="closeModal">
              OK
            </a-button>
          </div>
        </div>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import AppsCollapse from '@/views/apps/media/components/appsCollapse.vue'
// import NoImagePic from '@/views/apps/media/components/informers/noImagePic.vue'

import { routerPushWithFilters } from '@/services/utils/routerHelpers'
import { formatTimezone } from '@/services/utils/media'

export default {
  name: 'informerInfoModal',
  components: {
    AppsCollapse,
  },
  props: {
    informerInfo: {
      type: Object,
    },
    languages: {
      type: Array,
    },
    displays: {
      type: Object,
    },
  },
  computed: {
    informerContext() {
      return this.informerInfo.context
    },
    selectedInfo() {
      return this.informerContext.images[this.selectedLanguage]
    },
    selectedImage() {
      return this.selectedInfo.image || null
    },
    langOptions() {
      const langKeys = Object.keys(this.informerContext.images)
      return this.languages.filter(language => langKeys.includes(language.name)).map(language => ({
        name: language.desc,
        value: language.name,
      }))
    },
    isTechInformer() {
      return this.informerInfo.tag === 'maintenance'
    },
  },
  data: () => ({
    selectedLanguage: 'ru',
    showAllPeriods: false,
  }),
  created() {
    this.selectedLanguage = this.langOptions.length ? this.langOptions[0].value : null
  },
  watch: {
    langOptions(newOptions) {
      if (newOptions.length) {
        this.selectedLanguage = newOptions[0].value
      }
    },
  },
  methods: {
    formatTimezone,
    formatDate: function (date) {
      return this.$moment(date).format('DD.MM.YY HH:mm')
    },
    handleLangChange(selectedLangKey) {
      this.selectedLanguage = selectedLangKey
    },
    openEditPage() {
      routerPushWithFilters(this.$router, `/media/informers/edit/${this.informerContext.id}`)
    },
    closeModal() {
      this.$emit('close')
    },
    clipboardHandle(text, info) {
      this.$clipboard(info)
      this.$notification.success({
        message: `${text} successfully copied to clipboard`,
        description: info.slice(0, 50) + '...',
      })
    },
    stripHtml(htmlString) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlString

      return tempDiv.textContent || tempDiv.innerText || ''
    },
  },
}
</script>

<style lang="scss" src="../style.module.scss" />
